import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Field,
  Label,
} from "@headlessui/react";

import { useContext, useState } from "react";
import { IconChevronDown, IconCheckmark, IconX } from "@hawkins/assets";
import cx from "classnames";
import { AnimatePresence, motion } from "motion/react";
import { Lenis } from "lenis/react";
import { FormLabelsContext } from "./FormLabelsContext";

export const MultiSelect = ({
  label,
  options,
  value,
  onChange,
  required,
  invalid,
  name,
}: {
  label: string;
  options: Array<{ label: string; value: string }>;
  value: Array<string>;
  onChange: (value: Array<string>) => void;
  required?: boolean;
  invalid: boolean;
  name: string;
}) => {
  const otherLabels = useContext(FormLabelsContext);
  const [query, setQuery] = useState("");

  const filteredOptions =
    query === ""
      ? options
      : options.filter((o) =>
          o.value.toLowerCase().includes(query.toLowerCase())
        );

  const removeOption = (optionToRemove: string) => {
    onChange(value.filter((option) => option !== optionToRemove));
  };

  return (
    <Field>
      <div className="flex gap-2 items-center text-body-regular mb-2 ml-3">
        <Label>{label}</Label>
        {required && (
          <span className="italic text-body-small text-brand-cream text-opacity-30">
            {otherLabels.required}
          </span>
        )}
      </div>
      <div className="relative">
        <Combobox
          multiple
          immediate
          value={value}
          onChange={onChange}
          onClose={() => setQuery("")}
        >
          {({ open }) => (
            <div>
              <div
                className={cx(
                  "flex flex-wrap content-center items-center gap-1 w-full min-h-14 py-1 pl-3 pr-9 outline-none font-medium border rounded-lg bg-transparent border-brand-cream/30 aria-expanded:bg-brand-black aria-expanded:border-brand-cream focus:border-brand-cream peer",
                  // Notably, we cannot transition other properties or at other times
                  // because it would conflict with the illusion that is played into*:
                  // where the sibling appears to grow out from this element
                  !open &&
                    "transition-[background-color] duration-half delay-full",
                  invalid &&
                    "!border-brand-red bg-gradient-to-r from-brand-red/15 to-brand-red/15"
                )}
                aria-expanded={open}
              >
                {value.map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => removeOption(value)}
                    className="flex items-center bg-gray-200 text-gray-800 text-sm rounded-full px-2 py-1 gap-1 outline-none border border-transparent focus:border-brand-cream shrink-0"
                  >
                    {
                      options.filter((option) => option.value === value)[0]
                        ?.label
                    }
                    <IconX size="small" />
                  </button>
                ))}
                <ComboboxInput
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="flex-1 min-w-[8ch] bg-transparent border-none text-brand-cream focus:outline-none placeholder-brand-cream/50 leading-6 py-1"
                  placeholder={
                    value.length > 0 ? "" : otherLabels.selectPlaceholder
                  }
                  name={name}
                  onKeyDown={(e) => {
                    if (!e.currentTarget.value && e.key === "Backspace") {
                      removeOption(value[value.length - 1]);
                    }
                  }}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2.5">
                  <ComboboxButton className="group">
                    <IconChevronDown
                      size="small"
                      className={cx(
                        "transition-transform",
                        open && "rotate-180"
                      )}
                    />
                  </ComboboxButton>
                </div>
              </div>

              <AnimatePresence>
                {open && (
                  <ComboboxOptions
                    static
                    className={cx(
                      "absolute z-10 left-0 w-full text-brand-cream rounded-b-lg overflow-clip top-full pt-2 -mt-2 border-x border-b outline-none bg-brand-black border-brand-cream/30 peer-aria-expanded:border-brand-cream peer-focus:border-brand-cream",
                      invalid &&
                        "!border-brand-red bg-gradient-to-r from-brand-red/15 to-brand-red/15"
                    )}
                    as={motion.div}
                    initial={{ height: 0 }}
                    animate={{ height: "auto" }}
                    exit={{ height: 0 }}
                  >
                    <Lenis className="overflow-y-auto max-h-[50vh]">
                      {filteredOptions.map((option, index) => (
                        <ComboboxOption
                          key={index}
                          value={option.value}
                          className="relative cursor-default select-none py-1.5 px-3 duration-half bg-transparent transition-colors data-[focus]:bg-brand-cream/10 text-brand-cream flex items-center gap-2 group"
                        >
                          <IconCheckmark
                            size="small"
                            className="transition-opacity opacity-0 group-data-[selected]:opacity-100"
                          />
                          <span className="block truncate group-data-[selected]:font-bold font-medium">
                            {option.label}
                          </span>
                        </ComboboxOption>
                      ))}
                    </Lenis>
                  </ComboboxOptions>
                )}
              </AnimatePresence>
            </div>
          )}
        </Combobox>
      </div>
    </Field>
  );
};
